"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Star, MoveHorizontal, Quote, MapPin } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Story {
  id: number;
  beforeImg: string;
  afterImg: string;
  client: string;
  location: string;
  quote: string;
  service: string;
}
const stories: Story[] = [{
  id: 1,
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/driveway_cracked_stained_before.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/driveway_sealed_pristine_after.png",
  client: "James & Sarah Peterson",
  location: "North Shore",
  quote: "Island Seals transformed our driveway and it looks brand new! The difference is night and day. Highly recommend their professional service for anyone dealing with weather-worn concrete.",
  service: "Driveway Restoration"
}, {
  id: 2,
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/pool_deck_faded_before.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/pool_deck_waterproofed_after.png",
  client: "The Pritchard family",
  location: "Coastal District",
  quote: "Our pool deck was becoming a safety hazard with slippery, worn stone. The team not only restored the look but added a non-slip seal that gives us peace of mind for our guests.",
  service: "PROJECT: RESIDENTIAL POOL DECK"
}, {
  id: 3,
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/patio_mossy_stone_before.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/patio_cleaned_sealed_after.png",
  client: "Elena Hudson",
  location: "Highland Gardens",
  quote: "I didn't realize how much the moss and grime were damaging my patio stone until I saw the results. It's like I have a whole new outdoor living space now.",
  service: "Residential Stone Care"
}];
const StarRating = () => {
  return <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => <Star key={star} className="size-4 fill-[var(--ring)] text-[var(--ring)]" />)}
    </div>;
};
const BeforeAfterSlider = ({
  beforeImg,
  afterImg,
  alt
}: {
  beforeImg: string;
  afterImg: string;
  alt: string;
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = Math.max(0, Math.min(x / rect.width * 100, 100));
      setSliderPosition(percentage);
    }
  }, []);
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) handleMove(e.clientX);
  }, [isDragging, handleMove]);
  const onTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging) handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);
  const onMouseUp = useCallback(() => setIsDragging(false), []);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("touchend", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [isDragging, onMouseMove, onMouseUp, onTouchMove]);
  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setIsDragging(true);
  };
  return;
};
export default function SuccessStories() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="success-stories" ref={sectionRef} className={`py-20 bg-[var(--muted)] transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--foreground)] mb-4">
            Real Results, Real Protection
          </h2>
          <p className="text-lg text-[var(--muted-foreground)]"><br /></p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full max-w-6xl mx-auto">
          <CarouselContent className="-ml-4">
            {stories.map((story, i) => <CarouselItem key={story.id} className="pl-4 md:basis-full lg:basis-full" data-index={i}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-2">
                  {}
                  <div className="w-full">
                    <BeforeAfterSlider beforeImg={story.beforeImg} afterImg={story.afterImg} alt={story.service} />
                  </div>

                  {}
                  <div className="h-full flex items-center">
                    <Card className="w-full h-full lg:h-auto bg-[var(--card)] border-[var(--border)] shadow-lg relative overflow-hidden">
                      {}
                      <div className="absolute top-6 right-6 text-[var(--muted)]/50 pointer-events-none">
                        <Quote className="size-24 rotate-180" />
                      </div>

                      <CardContent className="p-8 md:p-10 flex flex-col justify-center h-full relative z-10">
                        <div className="mb-6">
                          <StarRating />
                        </div>

                        <blockquote className="text-xl md:text-2xl font-medium text-[var(--card-foreground)] leading-relaxed mb-8">
                          "{story.quote}"
                        </blockquote>

                        <div className="flex items-center gap-4 mt-auto">
                          <div className="size-12 rounded-full bg-[var(--primary)]/10 flex items-center justify-center text-[var(--primary)] font-bold text-lg">
                            {story.client.charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-[var(--foreground)]">
                              {story.client}
                            </div>
                            
                          </div>
                        </div>
                        
                        <div className="mt-6 pt-6 border-t border-[var(--border)]">
                            <span className="text-xs font-semibold tracking-wider uppercase text-[var(--primary)]">
                                Project: {story.service}
                            </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="hidden md:block">
            
            
          </div>
          
          {}
          <div className="flex justify-center gap-2 mt-8 md:hidden">
             <div className="text-sm text-[var(--muted-foreground)] flex items-center gap-2">
                <MoveHorizontal className="size-4 animate-pulse" />
                <span>Swipe to see more stories</span>
             </div>
          </div>
        </Carousel>
      </div>
    </section>;
}